import React from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Hand } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Przekazujemy handleDrawTask jako prop
export default function WhoIsMostLikelyScreen({ handleDrawTask }) {
  const { state, dispatch } = useGame();
  const currentQuestion = state.currentQuestion;

  const currentPlayer = state.players[state.currentPlayerIndex];

  // Jeśli nie ma graczy (np. po resecie), wróć do startu
  if (!currentPlayer) {
    dispatch({ type: "SET_SCREEN", payload: "start" });
    return null; // Zwróć null, aby uniknąć błędów renderowania
  }

  // Zmieniamy nazwę - RESET wraca do *początku*, nie do startu
  const goBackToMenu = () => {
    dispatch({ type: "GO_BACK_TO_START" });
  };

  // Funkcja do losowania - woła prop, wskazując, by NIE pokazywać dialogu
  const draw = () => {
    if (handleDrawTask) {
      handleDrawTask(null, false); // Przekazujemy null (domyślny typ) i false (nie pokazuj dialogu)
    }
  };

  // Funkcja do przejścia do następnej tury
  const next = () => {
    dispatch({ type: "NEXT_TURN" });
    // Reducer 'NEXT_TURN' powinien automatycznie czyścić 'currentQuestion'
  };

  // Funkcja do czyszczenia pytania bez przechodzenia tury (np. do głosowania)
  const clearQuestion = () => {
    dispatch({ type: "SET_CURRENT_QUESTION", payload: null });
  };

  // Funkcja do usuwania "Wskaż osobę, która (najprawdopodobniej)" dla lepszego wyświetlania
  const formatQuestionText = (text) => {
    if (!text) return "";
    let formatted = text.replace(
      /^Wskaż osobę, która najprawdopodobniej /i,
      ""
    );
    formatted = formatted.replace(/^Wskaż osobę, która /i, "");
    formatted = formatted.replace(/\?$/, ""); // Usuń znak zapytania z końca, jeśli jest
    formatted = formatted.replace(/\.$/, ""); // Usuń kropkę z końca, jeśli jest
    // Upewnij się, że pierwsza litera jest duża
    return formatted.charAt(0).toUpperCase() + formatted.slice(1) + "?";
  };

  return (
    <>
      <header className="w-full flex items-center justify-between mb-8">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Powrót do Menu Głównego?</AlertDialogTitle>
              <AlertDialogDescription>
                Czy na pewno chcesz wrócić do menu głównego? Twoja obecna gra
                zostanie zapisana i będziesz mógł/mogła ją później wznowić.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction onClick={goBackToMenu}>
                Tak, wróć
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* ... (Info o turze bez zmian) ... */}
        <div className="text-center">
          <div className="text-sm text-white/60">Tura gracza:</div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-bold">{currentPlayer?.name}</span>
          </div>
        </div>
        <div className="w-9"></div>
      </header>
      <motion.div
        key="whoIsMostLikely"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center justify-between text-center min-h-[500px] p-6 w-full bg-card/30 rounded-2xl shadow-lg backdrop-blur-sm border border-white/10"
      >
        {/* Stan PRZED wylosowaniem pytania */}
        {!currentQuestion ? (
          <div className="flex flex-col items-center justify-center flex-1">
            <Sparkles className="w-16 h-16 text-yellow-400 mb-6" />
            <h2 className="text-2xl font-bold mb-4">
              Teraz czyta{" "}
              <span className="text-primary">{currentPlayer.name}</span>!
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xs">
              Przygotuj się! Wylosuj pytanie i zobaczmy, kogo wskaże grupa!
            </p>
            <Button
              onClick={draw}
              size="lg"
              className="shadow-lg shadow-primary/30"
            >
              Losuj Pytanie
            </Button>
          </div>
        ) : (
          /* Stan PO wylosowaniu pytania */
          <div className="flex flex-col items-center justify-between flex-1 h-full w-full">
            {/* Nagłówek */}
            <h3 className="text-sm uppercase text-muted-foreground tracking-widest">
              Wskaż osobę, która...
            </h3>

            {/* Pytanie */}
            <div className="flex-1 flex items-center justify-center my-6">
              <p className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                {formatQuestionText(currentQuestion.text)}
              </p>
            </div>

            {/* Obszar głosowania / akcji */}
            <div className="mb-8 p-6 bg-black/20 rounded-lg w-full border border-white/5">
              <h4 className="font-semibold mb-2 flex items-center justify-center gap-2">
                <Hand className="w-5 h-5 text-amber-400" /> Czas na Głosowanie!
              </h4>
              <p className="text-sm text-muted-foreground">
                Wszyscy jednocześnie wskazują osobę, która najlepiej pasuje.
              </p>
              {/* TODO: Możesz tutaj dodać listę graczy do kliknięcia dla głosowania */}
            </div>

            {/* Przycisk Następna Tura */}
            <Button onClick={next} size="lg" variant="secondary">
              Następna Tura <ArrowRight className="size-5" />
            </Button>
          </div>
        )}
        {/* Info o czytającym (zawsze widoczne) */}
        <div className="text-xs text-muted-foreground mt-4 pt-4 border-t border-white/5 w-full">
          Czyta: {currentPlayer.name}
        </div>
      </motion.div>
    </>
  );
}
